import * as React from "react";
import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { safeError } from "@/lib/safeError";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  errorName: string;
  errorStack: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: "", errorName: "", errorStack: "" };
  }

  static getDerivedStateFromError(error: unknown): State {
    // Use safeError to NEVER access .name/.message directly - Safari fix
    const info = safeError(error);
    return { 
      hasError: true, 
      errorMessage: info.message, 
      errorName: info.name,
      errorStack: info.stack ?? ""
    };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    // Use safeError to NEVER access .name/.message directly - Safari fix
    const info = safeError(error);
    console.error("[ErrorBoundary]", info.name, info.message, info.raw);
    console.error("[ErrorBoundary] Component Stack:", errorInfo.componentStack);
    if (info.stack) console.error("[ErrorBoundary] error.stack:", info.stack);

    // Send to Sentry with component stack
    Sentry.withScope((scope) => {
      scope.setExtra("componentStack", errorInfo.componentStack);
      scope.setTag("errorBoundary", "true");
      Sentry.captureException(info.raw);
    });
  }

  handleReload = () => {
    // ONLY use window.location.reload() - NO navigate() or router hooks
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, errorMessage: "", errorName: "", errorStack: "" });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // PURE UI ONLY - no hooks, no navigation, no side effects
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                Something went wrong
              </h1>
              <p className="text-muted-foreground">
                {this.state.errorMessage || "An unexpected error occurred"}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReset} variant="outline" type="button">
                Try Again
              </Button>
              <Button onClick={this.handleReload} className="gap-2" type="button">
                <RefreshCw className="h-4 w-4" />
                Reload Page
              </Button>
            </div>

            {import.meta.env.DEV && (this.state.errorName || this.state.errorStack) && (
              <details className="text-left mt-4 p-4 bg-muted rounded-lg">
                <summary className="cursor-pointer text-sm font-medium">
                  Error Details
                </summary>
                <pre className="mt-2 text-xs overflow-auto whitespace-pre-wrap">
                  {this.state.errorName}: {this.state.errorMessage}
                  {this.state.errorStack ? `\n\n${this.state.errorStack}` : ""}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
